import type { RootState } from "@/app/store";
import {
  setProperty,
  setPropertyFieldRules,
  updateCurrentSurvey,
  updatePropertyFieldRule,
  updateProperty as updatePropertyInStore,
} from "@features/survey/surveySlice";
import {
  PropertyFieldRule,
  PropertySortOrder,
  SurveyPropertyFieldRule,
  TeamLayoutPropertyFieldRule,
} from "@survey-types/survey.core.types";
import { Property } from "@survey-types/survey.property.types";
import { surveyApi } from "./base";
import { surveyCoreApi } from "./survey-core";

// Survey Properties API - handles property management
export const surveyPropertiesApi = surveyApi
  .enhanceEndpoints({
    addTagTypes: ["Survey"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      addNewProperty: build.mutation<
        Property,
        { AddPropertyFields: Partial<Property> }
      >({
        query: ({ AddPropertyFields }) => ({
          url: `/property`,
          method: "POST",
          body: { ...AddPropertyFields },
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data: createdProperty } = await queryFulfilled;

            // Trigger updateSurveyFlags after successful property creation
            // Uses updateSurveyFlags to prevent Survey tag invalidation and flickering
            dispatch(
              surveyCoreApi.endpoints.updateSurveyFlags.initiate({
                id: createdProperty.surveyId,
                marketMapBamData: '{"mapReset":true}',
              }),
            );
            dispatch(
              updateCurrentSurvey({
                id: createdProperty.surveyId,
                marketMapBamData: '{"mapReset":true}',
              }),
            );
          } catch (_error) {
            // Handle error if needed
            console.error("Error in addNewProperty");
          }
        },
        invalidatesTags: (result) => [{ type: "Survey", id: result?.surveyId }],
      }),
      reorderProperty: build.mutation<
        void,
        { surveyId: string; order: number; propertyId: string }
      >({
        query: ({ surveyId, order, propertyId }) => ({
          url: `/property/reorder`,
          method: "PATCH",
          body: { surveyId, order, propertyId },
        }),
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
        ],
      }),
      updateProperty: build.mutation<
        Property,
        { propertyId: string; updates: Partial<Property> }
      >({
        query: ({ propertyId, updates }) => ({
          url: `/property`,
          method: "PATCH",
          body: { ...updates, propertyId },
        }),
        async onQueryStarted(
          { propertyId: _propertyId, updates },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as RootState;
          const propertiesBackup = state.survey.currentSurvey?.properties ?? [];

          try {
            const { data: updatedProperty } = await queryFulfilled;
            dispatch(setProperty(updatedProperty));
            // Sync marketMapBamData if visibility changed
            if (updates?.isHidden !== undefined) {
              dispatch(
                surveyCoreApi.util.updateQueryData(
                  "getSurvey",
                  updatedProperty.surveyId,
                  (draft) => {
                    draft.marketMapBamData = '{"mapReset":true}';
                  },
                ),
              );
              // Sync marketMapBamData if visibility changed
              // Uses updateSurveyFlags to prevent Survey tag invalidation and flickering
              dispatch(
                surveyCoreApi.endpoints.updateSurveyFlags.initiate({
                  id: updatedProperty.surveyId,
                  marketMapBamData: '{"mapReset":true}',
                }),
              );
              dispatch(
                updateCurrentSurvey({
                  id: updatedProperty.surveyId,
                  marketMapBamData: '{"mapReset":true}',
                }),
              );
            }
            // Refresh updatedOn so the Share modal reflects Market Map edits
            // (move, style points, radius rings, hide/show) as they happen.
            dispatch(
              surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                surveyId: updatedProperty.surveyId,
              }),
            );
          } catch (_error) {
            dispatch(updateCurrentSurvey({ properties: propertiesBackup }));
            console.error("Error in updateProperty");
          }
        },
        // invalidatesTags removed: full optimistic update (RTK cache + Redux slice)
        // with rollback on error is in place. A tag invalidation would trigger a
        // redundant getSurvey refetch on every auto-save blur, causing unnecessary
        // network traffic and a re-render that overwrites the optimistic state.
      }),
      togglePropertyVisibility: build.mutation<
        Property,
        { propertyId: string; isHidden: boolean }
      >({
        query: ({ propertyId, isHidden }) => ({
          url: `/property`,
          method: "PATCH",
          body: { isHidden, propertyId },
        }),
        async onQueryStarted(
          { propertyId, isHidden },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as RootState;
          const surveyId = state.survey.currentSurvey?.id ?? "";
          const propertiesBackup = state.survey.currentSurvey?.properties ?? [];

          // Optimistically update RTK Query cache
          const cachePatch = dispatch(
            surveyCoreApi.util.updateQueryData(
              "getSurvey",
              surveyId,
              (draft) => {
                const property = draft.properties?.find(
                  (p) => p.propertyId === propertyId,
                );
                if (property) {
                  property.isHidden = isHidden;
                }
              },
            ),
          );

          // Optimistically update Redux slice
          dispatch(
            updatePropertyInStore({ propertyId, updates: { isHidden } }),
          );

          try {
            const { data: updatedProperty } = await queryFulfilled;

            // Use updateSurveyFlags instead of updateSurvey — it optimistically
            // patches both the RTK cache and Redux slice without invalidatesTags,
            // preventing a full getSurvey refetch that would overwrite store state.
            dispatch(
              surveyCoreApi.endpoints.updateSurveyFlags.initiate({
                id: updatedProperty.surveyId,
                marketMapBamData: '{"mapReset":true}',
              }),
            );
            // Refresh updatedOn so the Share modal reflects the visibility change
            dispatch(
              surveyCoreApi.endpoints.updateSurveyTimestamp.initiate({
                surveyId: updatedProperty.surveyId,
              }),
            );
          } catch (_error) {
            cachePatch.undo();
            dispatch(updateCurrentSurvey({ properties: propertiesBackup }));
            console.error("Error in togglePropertyVisibility");
          }
        },
        // No invalidatesTags — optimistic updates handle all state sync
      }),
      deleteProperty: build.mutation<
        void,
        { propertyId: string; surveyId: string }
      >({
        query: ({ propertyId }) => ({
          url: `/property/${propertyId}`,
          method: "DELETE",
          responseHandler: "text", // Handle response as text
        }),
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;

            // Access surveyId from the mutation arguments
            const { surveyId } = arg;

            // Trigger updateSurveyFlags after successful property deletion
            // Uses updateSurveyFlags to prevent Survey tag invalidation and flickering
            dispatch(
              surveyCoreApi.endpoints.updateSurveyFlags.initiate({
                id: surveyId,
                marketMapBamData: '{"mapReset":true}',
              }),
            );
            dispatch(
              updateCurrentSurvey({
                id: surveyId,
                marketMapBamData: '{"mapReset":true}',
              }),
            );
          } catch (_error) {
            // Handle error if needed
            console.error("Error in deleteProperty");
          }
        },
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
        ],
      }),
      reorderPropertyField: build.mutation<
        void,
        {
          surveyId: string;
          order: number;
          fieldId: string;
          propertyLayoutId: string;
        }
      >({
        query: ({ surveyId, order, fieldId }) => ({
          url: `/survey/propertyfieldrule/reorder`,
          method: "PATCH",
          body: { surveyId, order, fieldId },
        }),
        async onQueryStarted(
          { surveyId, order, fieldId, propertyLayoutId },
          { dispatch, queryFulfilled, getState },
        ) {
          const patch = dispatch(
            surveyCoreApi.util.updateQueryData(
              "getSurvey",
              surveyId,
              (draft) => {
                const rules =
                  draft.surveyPropertyFieldRule.propertyFieldRuleList;
                const currentIdx = rules.findIndex(
                  (f) => f.fieldId === fieldId,
                );
                if (currentIdx !== -1) {
                  const [item] = rules.splice(currentIdx, 1);
                  rules.splice(order, 0, item);
                  rules.forEach((f, i) => {
                    f.order = i;
                  });
                }
              },
            ),
          );
          try {
            await queryFulfilled;
            const state = getState() as unknown as RootState;
            const layoutId = state?.survey?.currentSurvey?.layoutId;
            if (layoutId !== propertyLayoutId) {
              dispatch(updateCurrentSurvey({ layoutId: propertyLayoutId }));
              dispatch(
                surveyCoreApi.endpoints.updateSurvey.initiate({
                  id: surveyId,
                  layoutId: propertyLayoutId,
                }),
              );
            }
          } catch {
            patch.undo();
          }
        },
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
        ],
      }),
      updatePropertyFieldRule: build.mutation<
        SurveyPropertyFieldRule,
        {
          surveyId: string;
          fieldRuleUpdates: Partial<PropertyFieldRule>;
          propertyLayoutId?: string;
        }
      >({
        query: ({ surveyId, fieldRuleUpdates }) => ({
          url: `/survey/propertyfieldrule/${surveyId}`,
          method: "PATCH",
          body: fieldRuleUpdates,
        }),
        async onQueryStarted(
          { surveyId, fieldRuleUpdates, propertyLayoutId },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as unknown as RootState;
          const fieldRules =
            state.survey.currentSurvey?.surveyPropertyFieldRule
              ?.propertyFieldRuleList ?? [];
          const originalField = fieldRules.find(
            (f) => f.fieldId === fieldRuleUpdates.fieldId,
          );

          // Optimistically update Redux store
          if (fieldRuleUpdates.fieldId && originalField) {
            dispatch(
              updatePropertyFieldRule({
                fieldId: fieldRuleUpdates.fieldId,
                isHidden: fieldRuleUpdates.isHidden ?? originalField.isHidden,
                status: (fieldRuleUpdates.status ?? originalField.status) as
                  | "ACTIVE"
                  | "INACTIVE",
                order: fieldRuleUpdates.order ?? originalField.order,
              }),
            );
          }

          try {
            await queryFulfilled;
            const layoutId = state?.survey?.currentSurvey?.layoutId;
            if (
              propertyLayoutId !== undefined &&
              layoutId !== propertyLayoutId
            ) {
              dispatch(updateCurrentSurvey({ layoutId: propertyLayoutId }));
              dispatch(
                surveyCoreApi.endpoints.updateSurvey.initiate({
                  id: surveyId,
                  layoutId: propertyLayoutId,
                }),
              );
            }
          } catch {
            if (fieldRuleUpdates.fieldId && originalField) {
              dispatch(
                updatePropertyFieldRule({
                  fieldId: originalField.fieldId,
                  isHidden: originalField.isHidden,
                  status: originalField.status as "ACTIVE" | "INACTIVE",
                  order: originalField.order,
                }),
              );
            }
          }
        },
      }),
      updatePropertyFieldRuleBulk: build.mutation<
        SurveyPropertyFieldRule,
        {
          surveyId: string;
          fieldRuleUpdates: Partial<PropertyFieldRule>[];
          propertyLayoutId: string;
        }
      >({
        query: ({ surveyId, fieldRuleUpdates }) => ({
          url: `/survey/propertyfieldrule/${surveyId}/bulk`,
          method: "PATCH",
          body: fieldRuleUpdates,
        }),
        async onQueryStarted(
          { surveyId, propertyLayoutId },
          { dispatch, queryFulfilled, getState },
        ) {
          const state = getState() as unknown as RootState;
          try {
            const response = await queryFulfilled;
            dispatch(setPropertyFieldRules(response.data));
            const layoutId = state?.survey?.currentSurvey?.layoutId;
            if (layoutId !== propertyLayoutId) {
              dispatch(updateCurrentSurvey({ layoutId: propertyLayoutId }));
              dispatch(
                surveyCoreApi.endpoints.updateSurvey.initiate({
                  id: surveyId,
                  layoutId: propertyLayoutId,
                }),
              );
            }
          } catch {
            console.error("Error in updatePropertyFieldRuleBulk mutation");
          }
        },
      }),

      importPropertiesFromExcel: build.mutation<
        {
          matched: string[];
          notMatched: string[];
          unParsedColumns: string[][];
        },
        { file: File; surveyId: string }
      >({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append("file", file);
          return {
            url: `/import`,
            method: "POST",
            body: formData,
          };
        },
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
        ],
      }),
      parseProperties: build.mutation<
        unknown,
        {
          unParsedColumns: string[][];
          headerMapping: Array<{
            header: string;
            matchedAttribute: string;
          }>;
        }
      >({
        query: ({ unParsedColumns, headerMapping }) => ({
          url: `/import/parseProperties`,
          method: "POST",
          body: {
            unParsedColumns,
            headerMapping,
          },
        }),
      }),
      bulkImportProperties: build.mutation<
        { importedProperties: Property[]; updatedSurvey: any },
        {
          surveyId: string;
          properties: Property;
        }
      >({
        query: ({ surveyId, properties }) => ({
          url: `/propertyFile/bulkImport`,
          method: "POST",
          body: {
            surveyId,
            properties,
          },
        }),
        // RTK Query will automatically invalidate and refetch the survey data
        // through the invalidatesTags property below
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
        ],
      }),
      updatePropertySortOrder: build.mutation<
        void,
        { surveyId: string; propertySortOrder: PropertySortOrder }
      >({
        query: ({ surveyId, propertySortOrder }) => ({
          url: `/survey`,
          method: "PATCH",
          body: { id: surveyId, propertySortOrder },
        }),
        async onQueryStarted(
          { surveyId, propertySortOrder },
          { dispatch, queryFulfilled },
        ) {
          // Optimistically update the redux store with the new sort order
          dispatch(updateCurrentSurvey({ propertySortOrder }));
          try {
            await queryFulfilled;
            // Invalidation via tag will re-fetch getSurvey with reordered properties
          } catch {
            // Rollback optimistic update on failure
            dispatch(
              surveyCoreApi.util.invalidateTags([
                { type: "Survey", id: surveyId },
              ]),
            );
          }
        },
        invalidatesTags: (_result, _error, { surveyId }) => [
          { type: "Survey", id: surveyId },
        ],
      }),
      getLayoutsBySurvey: build.query<
        TeamLayoutPropertyFieldRule[],
        { surveyId: string; teamId: string }
      >({
        query: ({ surveyId, teamId }) =>
          `/survey/${surveyId}/layouts/team/${teamId}`,
      }),
    }),
  });

export const {
  useReorderPropertyMutation,
  useUpdatePropertyMutation,
  useTogglePropertyVisibilityMutation,
  useDeletePropertyMutation,
  useReorderPropertyFieldMutation,
  useUpdatePropertyFieldRuleMutation,
  useUpdatePropertyFieldRuleBulkMutation,
  useAddNewPropertyMutation,
  useImportPropertiesFromExcelMutation,
  useParsePropertiesMutation,
  useBulkImportPropertiesMutation,
  useUpdatePropertySortOrderMutation,
  useGetLayoutsBySurveyQuery,
} = surveyPropertiesApi;
